import { useMemo, useState } from 'react';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Search, X } from 'lucide-react';

/**
 * Searchable checkbox list for picking many workers / participants.
 * items: [{ id, name, hint?, disabled?, disabledLabel? }]
 */
export const MultiPickList = ({ items, selected, onChange, placeholder = 'Search…', emptyLabel = 'None selected', testId }) => {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? items.filter(i => i.name?.toLowerCase().includes(s)) : items;
  }, [items, q]);

  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  };

  const chips = items.filter(i => selected.includes(i.id));

  return (
    <div className="mt-1 rounded-lg border border-slate-200 bg-white" data-testid={testId}>
      <div className="flex flex-wrap gap-1 p-2 min-h-[38px] border-b border-slate-100">
        {chips.length === 0 && <span className="text-xs text-slate-400 self-center">{emptyLabel}</span>}
        {chips.map(c => (
          <span key={c.id} className="inline-flex items-center gap-1 rounded-full bg-teal-50 text-teal-800 border border-teal-200 px-2 py-0.5 text-xs" data-testid={`${testId}-chip-${c.id}`}>
            {c.name}
            {!c.disabled && (
              <button type="button" onClick={() => toggle(c.id)} className="hover:text-rose-600" aria-label={`Remove ${c.name}`}>
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
      </div>
      <div className="relative px-2 pt-2">
        <Search className="absolute left-4 top-1/2 mt-1 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} className="h-8 pl-8 text-xs" data-testid={`${testId}-search`} />
      </div>
      <div className="max-h-40 overflow-y-auto p-2 space-y-0.5">
        {filtered.length === 0 && <p className="text-xs text-slate-400 px-1 py-2">No matches</p>}
        {filtered.map(i => {
          const checked = selected.includes(i.id);
          return (
            <label
              key={i.id}
              className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer transition-colors ${checked ? 'bg-teal-50' : 'hover:bg-slate-50'} ${i.disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
              data-testid={`${testId}-option-${i.id}`}
            >
              <Checkbox checked={checked} disabled={i.disabled} onCheckedChange={() => !i.disabled && toggle(i.id)} />
              <span className="flex-1 truncate">{i.name}</span>
              {i.hint && <span className="text-[10px] uppercase font-medium text-indigo-600">{i.hint}</span>}
              {i.disabled && i.disabledLabel && <span className="text-[10px] text-slate-400">{i.disabledLabel}</span>}
            </label>
          );
        })}
      </div>
    </div>
  );
};
